<template>
  <MainHeader />
  <MainSidebar />
  <div
    class="main-content bg_gray d-flex flex-column transition overflow-hidden"
  >
    <BreadcrumbMenu pageTitle="purchase_list" />
    
    <div class="row gx-0 mb-30 px-xl-40">
      <div class="col-md-6">
        <div class="filter-left d-flex align-items-center flex-wrap">
          <button
            type="button"
            class="btn style-two me-2 mb-2 mb-md-0"
            data-bs-toggle="modal"
            data-bs-target="#purchaseFilterModal"
          >
            {{ $t("purchase_page.filter") }} <i class="ri-filter-line ms-1"></i>
          </button>
          <div class="search-area position-relative w-sm-100 flex-grow-1 purchase-search-wrap" style="max-width: 300px;">
            <input
              type="text"
              v-model="searchQuery"
              :placeholder="$t('purchase_page.search_placeholder')"
              class="w-100 h-55 bg-white border-0 rounded-1 fs-14 text-black ps-3 pe-5 shadow-sm"
            />
            <button
              type="button"
              class="bg-transparent border-0 position-absolute top-50 end-0 translate-middle-y h-auto py-0 px-3 purchase-search-icon"
              tabindex="-1"
            >
              <i class="ri-search-line"></i>
            </button>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <div
          class="filter-right d-flex align-items-center justify-content-md-end flex-wrap"
        >
          <button @click="handleExport" class="btn btn-outline-success me-2 mb-2 mb-md-0 shadow-sm">
            <i class="ri-file-excel-line me-1"></i> {{ $t("purchase_page.excel") }}
          </button>
          <div class="btn btn-outline-info upload-btn me-2 mb-2 mb-md-0 shadow-sm position-relative overflow-hidden">
            <i class="ri-download-line me-1"></i> {{ $t("purchase_page.import") }}
            <input type="file" @change="handleImport" class="position-absolute top-0 start-0 opacity-0 w-100 h-100 cursor-pointer" />
          </div>
          <router-link to="/create-purchase" class="btn btn-primary shadow-sm">
            <i class="ri-add-circle-line me-1"></i> {{ $t("create_purchase") }}
          </router-link>
        </div>
      </div>
    </div>

    <PurchaseList 
      :purchases="purchases"
      :loading="loading"
      :pagination="tablePagination"
      :selected-ids="selectedIds"
      :format-date="formatDate"
      :get-due-amount="getDueAmount"
      :normalize-payment-status="normalizePaymentStatus"
      :normalize-purchase-status="normalizePurchaseStatus"
      :state-store="stateStore"
      @update:selected-ids="handleSelectedIdsUpdate"
      @table-change="handleTableChange"
      @view-details="handleViewDetails"
      @delete-purchase="deleteSinglePurchase"
      @delete-selected="deleteSelectedPurchases"
    />

    <!-- Filter Modal -->
    <div class="modal fade" id="purchaseFilterModal" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header border-bottom-dashed">
            <h5 class="modal-title fw-bold">{{ $t("purchase_page.advanced_filter") }}</h5>
            <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" :aria-label="$t('common.close')"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-12">
                <label class="form-label fs-14 text-title">{{ $t("purchase_page.payment_status") }}</label>
                <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.payment_status">
                  <option value="">{{ $t("purchase_page.all_status") }}</option>
                  <option value="paid">{{ $t("purchase_page.payment_status_paid") }}</option>
                  <option value="partial">{{ $t("purchase_page.payment_status_partial") }}</option>
                  <option value="pending">{{ $t("purchase_page.payment_status_pending") }}</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label fs-14 text-title">{{ $t("purchase_page.warehouse") }}</label>
                <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.warehouse_id">
                  <option value="">{{ $t("purchase_page.all_warehouses") }}</option>
                  <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label fs-14 text-title">{{ $t("purchase_page.supplier") }}</label>
                <select class="form-select bg-light border-0 fs-14 shadow-none" v-model="filters.supplier_id">
                  <option value="">{{ $t("purchase_page.all_suppliers") }}</option>
                  <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="modal-footer border-top-dashed justify-content-between">
            <button type="button" class="btn btn-light" @click="resetFilters">{{ $t("purchase_page.reset") }}</button>
            <button type="button" class="btn btn-primary" data-bs-dismiss="modal">{{ $t("purchase_page.apply_filter") }}</button>
          </div>
        </div>
      </div>
    </div>

    <div class="flex-grow-1"></div>
    <MainFooter />
  </div>
  <PurchaseDetails 
    :purchase="selectedPurchase" 
    @close="closeDetails"
    @payment-added="handlePaymentAdded"
    @purchase-deleted="handlePurchaseDeleted"
  />
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";
import MainHeader from "../../components/Layouts/MainHeader.vue";
import MainSidebar from "../../components/Layouts/MainSidebar.vue";
import BreadcrumbMenu from "../../components/Common/BreadcrumbMenu.vue";
import PurchaseList from "../../components/Purchases/PurchaseList/PurchaseList.vue";
import PurchaseDetails from "../../components/Purchases/PurchaseList/PurchaseDetails.vue";
import MainFooter from "../../components/Layouts/MainFooter.vue";
import { usePurchase } from "../../composables/usePurchase";
import { getPurchase, type Purchase } from "../../services/purchase";

const { t } = useI18n();
const selectedPurchase = ref<Purchase | null>(null);
const loadingDetails = ref(false);

const {
  purchases,
  loading,
  searchQuery,
  selectedIds,
  warehouses,
  suppliers,
  filters,
  pagination,
  stateStore,
  fetchDropdownData,
  fetchPurchases,
  handleTableChange,
  resetFilters,
  handleExport,
  handleImport,
  deleteSinglePurchase,
  deleteSelectedPurchases,
  formatDate,
  getDueAmount,
  normalizePaymentStatus,
  normalizePurchaseStatus,
} = usePurchase();

const tablePagination = computed(() => ({
  current: pagination.current,
  pageSize: pagination.pageSize,
  total: pagination.total,
  showSizeChanger: pagination.showSizeChanger,
  pageSizeOptions: pagination.pageSizeOptions,
  showTotal: pagination.showTotal,
}));

const handleViewDetails = async (purchase: Purchase) => {
  if (!purchase.id || loadingDetails.value) return;

  loadingDetails.value = true;

  try {
    const response = await getPurchase(purchase.id);
    selectedPurchase.value = response?.data ?? response;
  } catch (error) {
    console.error("Error loading purchase details:", error);
    Swal.fire({
      icon: "error",
      title: t("purchase_page.failed_to_load_purchase_details"),
      text: t("purchase_page.please_try_again"),
    });
  } finally {
    loadingDetails.value = false;
  }
};

const handleSelectedIdsUpdate = (value: number[]) => {
  selectedIds.value = value;
};

const closeDetails = () => {
  selectedPurchase.value = null;
};

const handlePaymentAdded = async (purchase?: Purchase) => {
  await fetchPurchases();
  if (purchase) {
    selectedPurchase.value = purchase;
  }
};

const handlePurchaseDeleted = async () => {
  await fetchPurchases();
  selectedPurchase.value = null;
};

onMounted(async () => {
  await fetchDropdownData();
  await fetchPurchases();
});
</script>

<style scoped>
.purchase-search-wrap input::placeholder {
  color: #6c757d;
}

.purchase-search-icon {
  color: #6c757d;
  pointer-events: none;
}
</style>
